import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  FlexibleConnectedPositionStrategyOrigin,
  Overlay,
  OverlayContainer,
  OverlayRef,
  ViewportRuler,
} from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import { Injectable, InjectionToken, Injector, OnDestroy } from '@angular/core';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { getRelativeDimensions } from '@app/finxone-web-frontend/app/lib/utils/get-relative-dimensions.utils';
import { ProviderClass } from '@finxone-platform/shared/sys-config-types';
import { debounceTime, fromEvent } from 'rxjs';
import { BaseComponent } from '../base/base.component';
import { CdkFlexibleConnectedPositionStrategy } from './cdk';

export const OVERLAY_ACTIVE_TYPE = new InjectionToken<boolean>('Overlay Active type');

@Injectable()
export abstract class OverlayWidgetComponent<T> extends BaseComponent implements OnDestroy {
  private overlayPortal: ComponentPortal<T>;
  private overlayRef: OverlayRef;
  constructor(
    public dataIn: ProviderClass<any>,
    private overlayService: Overlay,
    injector: Injector,
    component: ComponentType<T>,
    private _viewportRuler: ViewportRuler,
    private _platform: Platform,
    private _overlayContainer: OverlayContainer,
    protected configService: ConfigService,
  ) {
    super();

    if (this.overlay) {
      fromEvent(window, 'resize')
        .pipe(debounceTime(200))
        .subscribe(() => {
          this.positionUpdate();
        });
      //get the screen-container element
      const screenContainer = window.document.getElementById('web-app');
      if (screenContainer) {
        const containerWidth = screenContainer.clientWidth;
        const containerHeight = screenContainer.clientHeight;
        //get the positions from the RHS configuration
        const positions: ConnectedPosition[] = [
          {
            originX: this.originX,
            originY: this.originY,
            overlayX: this.overlayX,
            overlayY: this.overlayY,
            offsetX: this.offsetX,
            offsetY: this.offsetY,
          },
        ];
        //create the overlay using the positioning and dimensions we set
        this.overlayRef = this.overlayService.create({
          positionStrategy: setupWorkflowFlexibleConnectedPositionStrategy(
            containerWidth,
            containerHeight,
            positions,
            this._viewportRuler,
            this._platform,
            this._overlayContainer,
          ),
          //gets the dimensions relative to the phone rather than the workflow screen (for %'s vw/vh)
          width: getRelativeDimensions(containerWidth, this.width),

          height: getRelativeDimensions(containerHeight, this.height),
        });
        // Create ComponentPortal that can be attached to a PortalHost
        this.overlayPortal = new ComponentPortal(
          component,
          undefined,
          Injector.create({
            providers: [
              {
                provide: ProviderClass,
                useValue: {
                  ...this.dataIn,
                  attri: {
                    ...this.dataIn.attri,
                    overlay: false,
                    overlayActive: true,
                  },
                },
              },
            ],
            parent: injector,
          }),
        );

        this.overlayRef // Attach ComponentPortal to PortalHost
          .attach(this.overlayPortal);
      }
    }
  }

  private positionUpdate() {
    const screenContainer = window.document.getElementById('web-app');
    if (screenContainer) {
      const containerWidth = screenContainer.clientWidth;
      const containerHeight = screenContainer.clientHeight;
      //get the positions from the RHS configuration
      const positions: ConnectedPosition[] = [
        {
          originX: this.originX,
          originY: this.originY,
          overlayX: this.overlayX,
          overlayY: this.overlayY,
          offsetX: this.offsetX,
          offsetY: this.offsetY,
        },
      ];

      this.overlayRef.updatePositionStrategy(
        setupWorkflowFlexibleConnectedPositionStrategy(
          containerWidth,
          containerHeight,
          positions,
          this._viewportRuler,
          this._platform,
          this._overlayContainer,
        ),
      );
      this.overlayRef.updateSize({
        //gets the dimensions relative to the phone rather than the workflow screen (for %'s vw/vh)
        width: getRelativeDimensions(containerWidth, this.width),

        height: getRelativeDimensions(containerHeight, this.height),
      });
      this.overlayRef.updatePosition();
    }
  }

  public ngOnDestroy(): void {
    if (this.overlayPortal && this.overlayRef) {
      this.overlayRef.dispose();
    }
  }

  public get overlay(): boolean {
    if (this.dataIn?.attri && this.dataIn?.attri['overlay'] != null) return this.dataIn.attri['overlay'];
    else return false;
  }

  public get overlayActive(): boolean {
    if (this.dataIn?.attri && this.dataIn?.attri['overlayActive'] != null)
      return this.dataIn.attri['overlayActive'];
    else return false;
  }

  public get originX(): 'start' | 'center' | 'end' {
    if (this.dataIn.attri['originX'] != null) return this.dataIn.attri['originX'];
    else return 'start';
  }

  public get originY(): 'top' | 'center' | 'bottom' {
    if (this.dataIn.attri['originY'] != null) return this.dataIn.attri['originY'];
    else return 'top';
  }

  public get overlayX(): 'start' | 'center' | 'end' {
    if (this.dataIn.attri['overlayX'] != null) return this.dataIn.attri['overlayX'];
    else return 'start';
  }

  public get overlayY(): 'top' | 'center' | 'bottom' {
    if (this.dataIn.attri['overlayY'] != null) return this.dataIn.attri['overlayY'];
    else return 'top';
  }

  public get offsetX(): number {
    if (this.dataIn.attri['offsetX'] != null) return this.dataIn.attri['offsetX'];
    else return 0;
  }

  public get offsetY(): number {
    if (this.dataIn.attri['offsetY'] != null) return this.dataIn.attri['offsetY'];
    else return 0;
  }

  public get width(): string {
    if (this.dataIn.attri['width'] != null) return this.dataIn.attri['width'];
    else return '0px';
  }

  public get height(): string {
    if (this.dataIn.attri['height'] != null) return this.dataIn.attri['height'];
    else return '0px';
  }
}

function setupWorkflowFlexibleConnectedPositionStrategy(
  width: number,
  height: number,
  positions: ConnectedPosition[],
  _viewportRuler: ViewportRuler,
  _platform: Platform,
  _overlayContainer: OverlayContainer,
): FlexibleConnectedPositionStrategy {
  //override the origin of the overlay to use the device in the workflow instead of using the default (<body>)
  const overrideOrigin: FlexibleConnectedPositionStrategyOrigin = {
    x: 0,
    y: 0,
    width: width,
    height: height,
  };

  const scrollPosition = _viewportRuler.getViewportScrollPosition();

  return new CdkFlexibleConnectedPositionStrategy(
    {
      top: scrollPosition.top,
      left: scrollPosition.left,
      right: scrollPosition.left,
      bottom: scrollPosition.top,
      width: width,
      height: height,
    },
    overrideOrigin,
    _viewportRuler,
    document,
    _platform,
    _overlayContainer,
  )
    .setOrigin(overrideOrigin)
    .withFlexibleDimensions(false)
    .withPositions(positions);
}
